"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Clock, Calendar, ArrowRight } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardContent, CardFooter, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const getReadingTime = (content?: string) => {
  if (!content) return "3 min read";
  const text = content.replace(/<[^>]*>?/gm, "");
  const words = text.split(/\s+/).length;
  const minutes = Math.ceil(words / 200);
  return `${minutes} min read`;
};
const formatDate = (dateString?: string) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
};
function LatestIntelligenceConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="h-[300px] animate-pulse rounded-xl border border-border bg-card/50" />)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-muted-foreground mb-4">Failed to load intelligence data.</p>
        <Button onClick={refetch} variant="outline">
          Retry Connection
        </Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="py-12 text-center">
        <p className="text-muted-foreground">No intelligence reports found.</p>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
      {posts.map((post, i) => {
      const category = post.categories?.[0]?.name || "Insight";
      const readingTime = getReadingTime(post.content);
      const date = formatDate(post.date);
      return <Link key={post.id} to={post.link || "#"} className="group block h-full">
            <Card data-index={i} className="h-full border-border bg-card transition-all duration-300 hover:border-primary hover:shadow-[0_0_15px_-3px_var(--color-primary)]/20 group-hover:-translate-y-1">
              <CardHeader className="space-y-4 pb-2">
                <div className="flex justify-between items-start">
                  <Badge variant="outline" className="border-primary/20 text-primary bg-primary/5 font-mono tracking-wider text-[10px] uppercase">
                    {category}
                  </Badge>
                </div>
                <CardTitle className="font-mono text-xl leading-tight tracking-tight group-hover:text-primary transition-colors">
                  {post.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="line-clamp-3 text-muted-foreground/80">
                  <div dangerouslySetInnerHTML={{
                __html: post.excerpt || ""
              }} />
                </CardDescription>
              </CardContent>
              <CardFooter className="mt-auto pt-4 border-t border-border/50 flex items-center justify-between text-xs text-muted-foreground font-mono">
                <div className="flex items-center gap-4">
                  <span className="flex items-center gap-1.5">
                    <Clock className="size-3.5" />
                    {readingTime}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <SafeHydrate><Calendar className="size-3.5" /></SafeHydrate>
                    {date}
                  </span>
                </div>
                <ArrowRight className="size-4 opacity-0 -translate-x-2 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0 text-primary" />
              </CardFooter>
            </Card>
          </Link>;
    })}
    </div>;
}
export default function LatestIntelligence() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="latest-intelligence" className="dark bg-background text-foreground py-20 px-4 md:px-8 border-t border-border">
      <div className="mx-auto max-w-7xl">
        <div className="mb-12 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="space-y-2">
            <h2 className="font-mono text-3xl md:text-4xl font-bold tracking-tighter text-foreground">
              Latest Intelligence
            </h2>
            <p className="text-muted-foreground max-w-xl">
              Technical deep dives, performance audits, and engineering insights for the modern web.
            </p>
          </div>
          <Link to="/LatestInsightsPage">
            <Button variant="outline" className="font-mono text-xs uppercase tracking-wider border-primary/20 hover:border-primary hover:bg-primary/5 hover:text-primary">
              View Archive
            </Button>
          </Link>
        </div>

        <PostsProvider wp_query={wp_query}>
          <LatestIntelligenceConsumer />
        </PostsProvider>
      </div>
    </section>;
}